<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <ui-segment
        v-model="type"
        tabs
        :options="[
          { value: 'orders', label: $t('orders') },
          { value: 'meals', label: $t('report_group.meals_view') },
          { value: 'foods', label: $t('meals_main') },
        ]"
        :label="$t('view')"
      />
    </report-tools>

    <report-state v-if="loading && !reportItem.length" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-truck-delivery-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      :columns="columns"
      :rows="rows"
      :sort="sort"
      :totals="footTotals"
      :total-label="$t('total')"
      @sort="sortBy"
    >
      <template #head-shift="{ column }">
        <report-shift-tag :shift="column.shift.id"></report-shift-tag>
      </template>

      <template #cell-shift="{ row, column }">
        <span
          v-if="row[column.key].ordered"
          class="rd-cellbar"
          :class="{ 'is-complete': row[column.key].complete }"
        >
          <span class="rd-cellbar__text">{{ row[column.key].ordered }} / {{ row[column.key].taken }}</span>
          <span class="rd-cellbar__bar"><i :style="{ width: `${row[column.key].percent}%` }"></i></span>
        </span>
        <span v-else class="is-zero">–</span>
      </template>

      <template #cell-ordered="{ row }">
        <b>{{ row.ordered }}</b> / {{ row.taken }}
        <span class="rd-mini" :class="{ 'is-complete': row.complete }">
          <i :style="{ width: `${row.percent}%` }"></i>
        </span>
      </template>

      <template #cell-actions="{ row }">
        <div class="rd-tbl__actions">
          <button type="button" class="ui-btn ui-btn--sm" :aria-label="$t('employees')" @click="openCompanyEmployees(row.source)">
            <v-icon size="16">mdi-account-multiple-outline</v-icon>
            <span class="rd-btn__label">{{ $t("employees") }}</span>
          </button>
          <button
            type="button"
            class="ui-btn ui-btn--sm"
            :aria-label="$t('monthly')"
            @click="openDialogReportFirm(row.source)"
          >
            <v-icon size="16">mdi-calendar-month-outline</v-icon>
            <span class="rd-btn__label">{{ $t("monthly") }}</span>
          </button>
        </div>
      </template>
    </ui-table>

    <report-group-meals-taken-company-monthly-orders
      v-model="dialogFirm"
      :firm-report-item="firmReportItem"
    ></report-group-meals-taken-company-monthly-orders>
  </div>
</template>

<script>
import ReportGroupMealsTakenCompanyMonthlyOrders from "@/components/report/group-meals/dialogs/ReportGroupMealsTakenCompanyMonthlyOrders.vue";
import ReportTabGroupMealsTabTaken from "@/components/report/group-meals/ReportTabGroupMealsTabTaken.vue";
import { SHIFTS } from "@/constants/shifts";
import hasShiftBinding from "../../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../../shared/hasShiftSubtitle";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import hasGroupTakenRows from "../../../shared/group/hasGroupTakenRows";
import { copyText, rowsToTsv } from "../../copyTable";
import hasTableSort from "../../hasTableSort";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";

/**
 * Naručeno / Preuzeto (jela iz grupa) na računaru: firma × smena, naručeno / preuzeto sa trakom napretka, ukupno i
 * razlika. "Narudžbe / Jela" je prekidač u alatima, smene su u zajedničkoj traci. "Radnici" vodi na Korisnike
 * te firme, "Mesečno" otvara mesečni pregled firme (dijalog kao do sada). Nasleđuje učitavanje, štampu i Excel iz
 * starog taba.
 */
export default {
  name: "ReportDesktopGroupTaken",
  extends: ReportTabGroupMealsTabTaken,
  mixins: [hasShiftBinding, hasShiftSubtitle, hasTableSort, hasGroupTakenRows],
  components: { ReportGroupMealsTakenCompanyMonthlyOrders, ReportShiftTag, ReportState, ReportTools },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("firm"), name: true, sortable: true },
        ...this.visibleShifts.map((shift) => ({ key: shift.key, slot: "shift", shift, align: "end" })),
        { key: "ordered", label: this.$t("total"), align: "end", num: true, sortable: true },
        {
          key: "difference",
          label: this.$t("difference"),
          align: "end",
          num: true,
          zero: true,
          sortable: true,
          cellClass: (row) => ({ "is-alert": row.difference > 0 }),
        },
        { key: "actions", label: "", align: "end", headCls: "rd-col-actions" },
      ];
    },
    /** Zbirni red (tekst): naručeno / preuzeto po smeni i ukupno, razlika. */
    footTotals() {
      const totals = { ordered: `${this.summary.ordered} / ${this.summary.taken}`, difference: this.summary.difference };
      this.visibleShifts.forEach((shift) => {
        const sum = this.summary[shift.key];
        totals[shift.key] = sum.ordered ? `${sum.ordered} / ${sum.taken}` : "–";
      });
      return totals;
    },
    /** Smene 1–3 uvek (ako su izabrane), četvrta samo kad neka firma ima obroke u njoj. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.selectedShifts.includes(shift.id) && (shift.id < 4 || this.total.some((item) => item[shift.key]?.quantity > 0))
      );
    },
    rows() {
      return this.sortRows(this.matchedRows);
    },
    summary() {
      return this.summarize(this.rows);
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("no_taken_group_meals_data");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "ordered", label: this.$t("ordered"), value: this.summary.ordered, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.summary.taken },
        { key: "difference", label: this.$t("difference"), value: this.summary.difference },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    /** Radnici firme: ista firma u Korisnicima (isti izbor u store-u kao i u starom tabu). */
    openCompanyEmployees(item) {
      this.selectCompanyForUsers(item);
      this.$emit("open-screen", "groupUsers");
    },
    async copyTable() {
      const headers = [
        this.$t("firm"),
        ...this.visibleShifts.map((shift) => `${this.$t(shift.label)} (${this.$t("ordered_taken_meals")})`),
        `${this.$t("total")} (${this.$t("ordered_taken_meals")})`,
        this.$t("difference"),
      ];
      const rows = this.rows.map((row) => [
        row.name,
        ...this.visibleShifts.map((shift) => `${row[shift.key].ordered} / ${row[shift.key].taken}`),
        `${row.ordered} / ${row.taken}`,
        row.difference,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
